<!--
Copyright 2017 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <tr class="form-row">
    <td class="name">
      <link-if-can :to="primaryFormPath(form)">
        <span>{{ form.nameOrId() }}</span><span class="icon-angle-right"></span>
      </link-if-can>
    </td>
    <td v-if="columns.has('idAndVersion')" class="id-and-version">
      <div class="form-id">
        <span :title="form.xmlFormId">{{ form.xmlFormId }}</span>
      </div>
      <div v-if="form.version != null && form.version !== ''" class="version">
        <span :title="form.version">{{ form.version }}</span>
      </div>
    </td>
    <td v-if="columns.has('submissions')" class="submissions">
      <div>
        <router-link :to="submissionsPath">
          <span>{{ $tcn('count.submission', form.submissions) }}</span>
          <span class="icon-angle-right"></span>
        </router-link>
      </div>
      <div v-if="form.lastSubmission != null">
        <router-link :to="submissionsPath">
          <i18n :tag="false" path="lastSubmission">
            <template #dateTime>
              <date-time :iso="form.lastSubmission"/>
            </template>
          </i18n>
        </router-link>
      </div>
    </td>
    <td v-if="columns.has('actions')" class="actions">
      <template v-if="form.publishedAt != null">
        <enketo-preview v-if="project.permits('project.update')"
          :form-version="form"/>
        <enketo-fill v-else :form-version="form">
          <span class="icon-edit"></span>{{ $t('action.fill') }}
        </enketo-fill>
      </template>
    </td>
  </tr>
</template>

<script>
import DateTime from '../date-time.vue';
import EnketoFill from '../enketo/fill.vue';
import EnketoPreview from '../enketo/preview.vue';
import LinkIfCan from '../link-if-can.vue';
import Form from '../../presenters/form';
import routes from '../../mixins/routes';
import { requestData } from '../../store/modules/request';

export default {
  name: 'FormRow',
  components: { DateTime, EnketoFill, EnketoPreview, LinkIfCan },
  mixins: [routes()],
  props: {
    form: {
      type: Form,
      required: true
    },
    columns: {
      type: Set,
      required: true
    }
  },
  computed: {
    // The component assumes that this data will exist when the component is
    // created.
    ...requestData(['project']),
    submissionsPath() {
      return this.formPath(
        this.form.projectId,
        this.form.xmlFormId,
        this.form.publishedAt != null ? 'submissions' : 'draft/testing'
      );
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/scss/variables';

.form-row {
  .table tbody & td { vertical-align: middle; }

  .name {
    .link-if-can { font-size: 30px; }

    a {
      &, &:hover, &:focus {
        color: inherit;
        text-decoration: none;
      }

      .icon-angle-right {
        font-size: 20px;
        margin-left: 12px;
        vertical-align: 2px;
      }
    }
  }

  .form-id, .version {
    font-family: $font-family-monospace;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .version { color: #888; }

  .submissions {
    a {
      &, &:hover, &:focus {
        color: inherit;
        text-decoration: none;
      }
    }

    .icon-angle-right { margin-left: 6px; }
  }
}
</style>

<i18n lang="json5">
{
  "en": {
    // This text shows when the last Submission was received. {dateTime} shows
    // the date and time, for example: "2020/01/01 01:23". It may show a
    // formatted date like "2020/01/01", or it may use a word like "today",
    // "yesterday", or "Sunday".
    "lastSubmission": "(last {dateTime})",
    "action": {
      "fill": "Fill Form"
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "lastSubmission": "(poslední {dateTime})",
    "action": {
      "fill": "Vyplnit formulář"
    }
  },
  "de": {
    "lastSubmission": "(zuletzt {dateTime})",
    "action": {
      "fill": "Formular ausfüllen"
    }
  },
  "es": {
    "lastSubmission": "(último {dateTime})",
    "action": {
      "fill": "Llenar formulario"
    }
  },
  "fr": {
    "lastSubmission": "(dernière {dateTime})",
    "action": {
      "fill": "Remplir le formulaire"
    }
  }
}
</i18n>
